/**
 * S-23 — Identity wire schemas (Identity :5101), Zod-parsed at the seam.
 * Mirrors UserSummaryDto / RoleSummaryDto field-by-field. Statuses mirror the
 * backend UserStatus constants — unknown status = contract drift, fails loudly.
 */
import { z } from 'zod';

import { dashboardFilterSchema } from './dashboardSegments';
import { pagedListSchema } from './education';

/**
 * Identity UserStatus constants (invited → active ⇄ disabled) — the statuses an admin may SET.
 * ⚠ Not the ones the wire can carry: see {@link userWireStatuses}.
 */
export const userStatuses = ['invited', 'active', 'disabled'] as const;

/**
 * USER-MERGE (mig 643): every status a user can be READ in — the settable three plus 'merging' (both users while a
 * merge applies) and 'merged' (the merged-away account). Only the merge coordinator enters or leaves those two.
 *
 * ⚠ TWO LISTS ON PURPOSE. The schemas parse this one — `z.enum(userStatuses)` would reject the WHOLE users page the
 * moment one account was merging (zod-enum-rejects-whole-response). The status picker maps `userStatuses`, so it
 * can never offer "merged" as a choice.
 */
export const userWireStatuses = [...userStatuses, 'merging', 'merged'] as const;

const isoDate = z.string().min(1);

// ⚠ DECLARED HERE, ABOVE ITS FIRST USE, AND THAT IS NOT A STYLE CHOICE. `userSummarySchema` below
// references this one (USR-COL put the placements on the roster row), and a `const` read before its
// declaration is a temporal-dead-zone throw at MODULE LOAD — not a type error, so tsc says nothing and
// every page importing this file blanks at once. It lived next to `userExtendedProfileSchema` while the
// profile was its only reader.
/**
 * AGECAT-PT (mig 472) — one age-band placement: "for this program type, that band".
 *
 * ⚠ BOTH IDS ARE OPAQUE AND THE PAIRING IS THE CLIENT'S TO GET RIGHT. Identity cannot check that
 * `ageCategoryId` belongs to `programTypeId` — the two live in the Definitions schema, across the
 * service boundary [STD-OWN] — so it stores whatever it is sent. Build these from the bands a program
 * type actually returned, never by pairing ids drawn from two independent lists.
 */
export const userAgeCategorySchema = z.object({
  programTypeId: z.guid(),
  ageCategoryId: z.guid()
});
export type UserAgeCategory = z.infer<typeof userAgeCategorySchema>;

/**
 * PPT-2 (mig 327) — one partner ROUTE on a list row: "for this program type, that partner company".
 *
 * ⚠ NOT `companyId`, AND THE TWO ANSWER FOR DIFFERENT PEOPLE. `users.company_id` is the partner
 * EMPLOYEE's employment link; a student's partner lives here, one per programme, because a student can
 * route English through one company and Maths through another. Reading either as the other reports the
 * other population as unaffiliated — which is a wrong answer, not a blank.
 *
 * ⚠ NO `companyName`, unlike {@link userPartnerSelectionSchema} further down, which answers the
 * uncached per-user endpoint. This rides on the CACHED user list, so the name is resolved client-side
 * from the company catalogue: a name projected into a cached read makes a company rename a staleness
 * bug with no writer naming that query.
 */
export const userSummaryPartnerSelectionSchema = z.object({
  programTypeId: z.guid(),
  companyId: z.guid()
});
export type UserSummaryPartnerSelection = z.infer<typeof userSummaryPartnerSelectionSchema>;

/** UserSummaryDto — parola/oturum alanları backend'de bilinçli dışarıda. */
export const userSummarySchema = z.object({
  id: z.guid(),
  /**
   * ⚠ NULLISH since STU-HANDLE (mig 314), and this one is load-bearing for a SURFACE, not a field. A student a
   * teacher created without an address has `email: null`, and a required string here throws inside
   * `pagedListSchema` — which fails the whole page, not one row. So the moment any tenant had a single
   * handle student, the ADMIN USER LIST stopped rendering entirely. Render `displayName`; treat null as
   * "signs in with a username".
   */
  email: z.string().nullish(),
  displayName: z.string(),
  status: z.enum(userWireStatuses),
  roles: z.array(z.string()),
  createdAt: isoDate,
  // CO-4: opaque company membership (null/absent = unassigned). Optional so older responses parse.
  companyId: z.guid().nullish(),
  /**
   * USR-NAME (mig 310) / STU-HANDLE (mig 314): the second login identifier. The server has always sent this
   * on this DTO; the schema stripped it, so the admin list had no way to answer "what does this user sign in
   * with" for a student who has a handle and NO e-mail. That is the only identifier such an account has.
   */
  username: z.string().nullish(),
  /** USER-CODE (mig 629): the 9-character user code, e.g. TE2700251. Null until the tenant saves a prefix. */
  userCode: z.string().nullish(),

  // ---- USR-COL: the admin user list's Country / School / Teacher / Age-category columns. ----
  //
  // ⚠ ALL NULLISH, AND THAT IS LOAD-BEARING TWICE OVER. First for rollout: this DTO is the response of
  // six COMMANDS as well as the list, and the server fills these on the LIST projection only (they live
  // on user_profiles, which the command handlers do not load) — a required field here would throw inside
  // `pagedListSchema` and take out a whole surface, the exact way a single handle-only student once
  // stopped the admin user list rendering (see `email` above). Second for deploy overlap: an older
  // Identity sends none of them.
  //
  // ⚠ IDS, NOT NAMES — resolve them on the client. countryDefinitionId and the band ids name rows in
  // the Definitions service, which Identity may not read [STD-OWN]; school and teacher are ids by the
  // same rule the page already applies to `companyId`, so ONE lookup shape covers every reference column
  // instead of four.
  countryDefinitionId: z.guid().nullish(),
  schoolId: z.guid().nullish(),
  teacherUserId: z.guid().nullish(),
  // NAMES-SWEEP: …except the school and teacher NAMES, which the LIST now carries (same-schema joins in
  // Identity), because the client join (GET /users?ids= + /schools?ids=) failed on large tenants.
  // teacherName = FullName ?? Email ?? Username (userOptionLabel); schoolName = legal name. Null on
  // command ACKs and when the reference is unset or soft-deleted.
  teacherName: z.string().nullish(),
  schoolName: z.string().nullish(),

  // AGECAT-PT: one band per program type. Same nullish+transform as the profile's copy, so no caller
  // guards for null — `[]` means "no placements", on a list row and on a command ACK alike.
  ageCategories: z.array(userAgeCategorySchema).nullish().transform((v) => v ?? []),

  // PPT-2: the partner chosen per program type. Same nullish+transform as the placements above, and for
  // the same two reasons: a command ACK does not carry them, and an older Identity sends the field at
  // all. `[]` means "routes nowhere", which is what an unassigned student looks like.
  partnerSelections: z.array(userSummaryPartnerSelectionSchema).nullish().transform((v) => v ?? [])
});

/**
 * RoleSummaryDto.
 * RT-4: `roleType` is the field that matters now — `appSurface` is DERIVED from it server-side and
 * kept on the payload only until the `app_surface` column is dropped. Both nullish: a permission-only
 * role carries neither, and an older backend sends no `roleType` at all.
 */
export const roleSummarySchema = z.object({
  id: z.guid(),
  code: z.string(),
  name: z.string(),
  isSystem: z.boolean(),
  permissionCount: z.number(),
  userCount: z.number(),
  appSurface: z.string().nullish(),
  roleType: z.string().nullish(),
  // SP-ROLE (mig 501): may this role's holders be chosen as a salesperson? NULLISH, not `z.boolean()`,
  // for the same reason `roleType` is: an older backend sends no such field, and a required boolean
  // would make the whole ROLE LIST fail to parse against it rather than degrade.
  isSalesperson: z.boolean().nullish()
});

/** RoleDetailDto — izin kodları dahil (A5); RT-4: `roleType` + derived `appSurface`. */
export const roleDetailSchema = z.object({
  id: z.guid(),
  code: z.string(),
  name: z.string(),
  isSystem: z.boolean(),
  permissions: z.array(z.string()),
  userCount: z.number(),
  appSurface: z.string().nullish(),
  roleType: z.string().nullish(),
  isSalesperson: z.boolean().nullish() // SP-ROLE — see the summary schema
});

/**
 * RS-5 — App-surface (SPA) catalog. GET /api/v1/roles/app-surfaces → data: AppSurface[].
 * Rolün kullanıcılarını login sonrası HANGİ SPA'ya yönlendireceğini belirler (routing; izin VERMEZ).
 * Katalog tek kaynaktır — kodları FE'de sabitleme.
 */
export const appSurfaceSchema = z.object({
  code: z.string(),
  label: z.string()
});
export const appSurfaceListSchema = z.array(appSurfaceSchema);

/**
 * RT-5 — Role-type catalog. GET /api/v1/roles/role-types → data: RoleType[].
 *
 * The FOUR tenant-assignable types (`superadmin` is platform-only and never returned). Each entry
 * carries the SPA its holders land in, so the form can show that read-only next to the select
 * WITHOUT re-deriving the mapping client-side — the server stays the single source, and the two
 * cannot disagree. Do not hardcode the codes; render whatever the catalog returns.
 */
export const roleTypeSchema = z.object({
  code: z.string(),
  label: z.string(),
  appSurface: z.string(),
  /**
   * PT-7: the highest permission tier a role of this type may be granted. Nullish so an older
   * backend still parses — and a missing ceiling must disable the filter (show everything) rather
   * than hide everything.
   */
  ceiling: z.string().nullish()
});
export const roleTypeListSchema = z.array(roleTypeSchema);

/** PermissionSummaryDto — C-2 rol yazımında seçilebilir izin katalogu. */
export const permissionSummarySchema = z.object({
  id: z.guid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  isSystem: z.boolean()
});

export const userListSchema = pagedListSchema(userSummarySchema);

/**
 * PPT-COL — a row of a company's members list. Extends the user row with the program types that member
 * routes through THIS company.
 *
 * ⚠ Its own schema rather than a field on `userSummarySchema`, mirroring the server: the server's
 * `UserSummaryDto` header forbids partly-populated fields, and "types selected for this company" is not
 * merely absent on `/users` — there is no company in that request for it to be about.
 *
 * ⚠ `partnerProgramTypeIds` are OPAQUE `definitions` ids. Identity cannot read the definitions schema, so
 * labels are resolved client-side from the catalogue the page already loads. `nullish` + transform because a
 * member with no selection is the normal case and older responses omit the field entirely.
 */
export const companyMemberSchema = userSummarySchema.extend({
  partnerProgramTypeIds: z
    .array(z.guid())
    .nullish()
    .transform((v) => v ?? [])
});

export const companyMemberListSchema = pagedListSchema(companyMemberSchema);

/**
 * PPT-MEM — a row of the ASSIGNED USERS list: someone who routes at least one program type through this
 * partner. A different population from the members list, which is staff (`users.company_id`).
 *
 * ⚠ `email` nullish for the same reason as `userSummarySchema`: a student created by a teacher signs in with
 * a handle and has none, and a required string here throws inside `pagedListSchema` — failing the whole page
 * rather than one field.
 */
export const companyAssignedUserSchema = z.object({
  userId: z.guid(),
  firstName: z.string().nullish(),
  middleName: z.string().nullish(),
  lastName: z.string().nullish(),
  displayName: z.string(),
  email: z.string().nullish(),
  username: z.string().nullish(),
  roles: z.array(z.string()).nullish().transform((v) => v ?? []),
  /** Opaque `definitions` ids — Identity cannot read that schema, so the FE resolves the labels. */
  programTypeIds: z.array(z.guid()).nullish().transform((v) => v ?? []),
  countryDefinitionId: z.guid().nullish(),
  schoolId: z.guid().nullish(),
  schoolName: z.string().nullish()
});

/**
 * ⚠ `countsByProgramType` counts SELECTIONS, not users, so its values sum HIGHER than `totalItems` when
 * people route more than one type (demo: 53 + 5 across 55 people). Do not "correct" that arithmetic.
 */
export const companyAssignedUserListSchema = z.object({
  items: z.array(companyAssignedUserSchema).nullish().transform((v) => v ?? []),
  totalItems: z.number().int(),
  countsByProgramType: z.record(z.string(), z.number().int()).nullish().transform((v) => v ?? {})
});
export const roleListSchema = pagedListSchema(roleSummarySchema);
export const permissionListSchema = pagedListSchema(permissionSummarySchema);

// PA-3/PA-8 — grouped (module, action) permission catalog for the Roles matrix UI.
// GET /api/v1/permissions/catalog → data is an array of these module entries.
/** One standard action slot (the fixed enum tier): action token + full code + label. */
export const permissionCatalogActionSchema = z.object({
  action: z.string(), // 'view' | 'add' | 'update' | 'delete' | 'print'
  code: z.string(), // canonical dotted code, e.g. 'learning.courses.view'
  label: z.string(),
  description: z.string().nullish(),
  /**
   * PT-7: blast radius — 'self' | 'scoped' | 'tenant' | 'platform' (mig 316). The Roles matrix greys
   * anything above the selected role type's ceiling. NULLISH IS LOAD-BEARING: null means unclassified,
   * which is UNCONSTRAINED (grantable to any role), and an older backend omits the field entirely.
   * Treating a missing tier as "blocked" would hide every permission the day a new service ships.
   */
  tier: z.string().nullish()
});
/** One special (Tier-2) domain action: full code + label. */
export const permissionCatalogSpecialSchema = z.object({
  code: z.string(),
  label: z.string(),
  description: z.string().nullish(),
  tier: z.string().nullish() // PT-7 — see permissionCatalogActionSchema.tier
});
export const permissionCatalogModuleSchema = z.object({
  serviceCode: z.string(),
  moduleCode: z.string(),
  labelKey: z.string(),
  standard: z.array(permissionCatalogActionSchema).default([]),
  special: z.array(permissionCatalogSpecialSchema).default([])
});
export const permissionCatalogSchema = z.array(permissionCatalogModuleSchema);

export type UserSummary = z.infer<typeof userSummarySchema>;
export type CompanyMember = z.infer<typeof companyMemberSchema>;
export type CompanyAssignedUser = z.infer<typeof companyAssignedUserSchema>;
export type RoleSummary = z.infer<typeof roleSummarySchema>;
export type RoleDetail = z.infer<typeof roleDetailSchema>;
export type PermissionSummary = z.infer<typeof permissionSummarySchema>;
export type PermissionCatalogModule = z.infer<typeof permissionCatalogModuleSchema>;
export type PermissionCatalogAction = z.infer<typeof permissionCatalogActionSchema>;
export type PermissionCatalogSpecial = z.infer<typeof permissionCatalogSpecialSchema>;
export type AppSurface = z.infer<typeof appSurfaceSchema>;
export type RoleType = z.infer<typeof roleTypeSchema>; // RT-5
export type UserStatus = (typeof userStatuses)[number];

// =============================================================================
// CO — Company registry (Identity :5101). CompanyDto mirrored field-by-field.
// A company is an org grouping users belong to (1 user → 1 company). Optional
// fields are nullish so partial profiles parse. Status mirrors CompanyStatus.
// =============================================================================

/** Identity CompanyStatus constants. */
export const companyStatuses = ['active', 'inactive'] as const;
export type CompanyStatus = (typeof companyStatuses)[number];

/**
 * SIGNUP-SCHOOL-NEW (mig 432) — a SCHOOL has a third status a company does not: `pending`, a school a
 * visitor described on the signup form that no administrator has looked at yet.
 *
 * ⚠ ITS OWN VOCABULARY, NOT A THIRD ENTRY ON `companyStatuses`. The two lists were shared, and school
 * rows parsed `z.enum(companyStatuses)` — so the first pending school made the whole admin Schools
 * page fail zod, which blanks the LIST, not the cell. Widening the shared list would have fixed that
 * by offering companies a status the Identity server rejects with a 400.
 *
 * The admin surface reads this: the list's status filter, the chip, and the edit form's dropdown —
 * where moving a school from `pending` to `active` IS the approval.
 */
export const schoolStatuses = ['active', 'inactive', 'pending'] as const;
export type SchoolStatus = (typeof schoolStatuses)[number];

/** CompanyType constants. 'partner' (ex 'affiliated_fair') is user-assignable; 'school' is now a
 *  separate entity (identity.schools), not a company type. A parent shares this type. */
export const companyTypes = ['partner', 'organizer'] as const;
export type CompanyType = (typeof companyTypes)[number];

/** CompanyDto — the full limited-company profile. */
export const companySchema = z.object({
  id: z.guid(),
  legalName: z.string(),
  tradingName: z.string().nullish(),
  // Classification. Older responses may omit it → default to the user-assignable 'partner' bucket.
  type: z.enum(companyTypes).default('partner'),
  registrationNumber: z.string().nullish(),
  taxId: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  websiteUrl: z.string().nullish(),
  addressLine1: z.string().nullish(),
  addressLine2: z.string().nullish(),
  stateRegion: z.string().nullish(),
  postalCode: z.string().nullish(),
  // DG-1: geography by definition id (opaque refs to definitions.definitions.id).
  countryDefinitionId: z.guid().nullish(),
  cityDefinitionId: z.guid().nullish(),
  districtDefinitionId: z.guid().nullish(),
  // CO-C1: primary contact person (single embedded contact; all optional).
  contactPersonName: z.string().nullish(),
  contactPersonTitle: z.string().nullish(),
  contactPersonEmail: z.string().nullish(),
  contactPersonPhone: z.string().nullish(),
  status: z.enum(companyStatuses),
  // CO-salesperson: the salesperson accountable for this company (ANY live user). salespersonId is
  // present on LIST rows too (feeds the school partner→salesperson auto-fill). CO-SP-LIST: the name now rides
  // on list rows as well (the Companies Salesperson column), not only on detail.
  salespersonId: z.guid().nullish(),
  salespersonName: z.string().nullish(),
  // CO-H1 hierarchy: nullable parent (self-reference). parentLegalName + childCount are populated
  // on the detail response only (the list leaves them null/0).
  parentCompanyId: z.guid().nullish(),
  parentLegalName: z.string().nullish(),
  childCount: z.number().default(0),
  // CTERM (mig 296/297): the agreed commercial terms. riskLevelDefinitionId is an OPAQUE definitions
  // ref (group risk_levels) — resolve the label from the definitions catalog, never assume a code.
  // commission/incentive are null when NOTHING has been agreed; 0 means agreed-at-zero, so render
  // the two differently. isPercentage is the UNIT of BOTH figures; it defaults to true because a
  // response from a server that predates these fields must still parse.
  riskLevelDefinitionId: z.guid().nullish(),
  commission: z.number().nullish(),
  incentive: z.number().nullish(),
  isPercentage: z.boolean().default(true),
  createdAt: isoDate,
  updatedAt: isoDate
});

/** CTERM: the definitions group backing the company risk band (mig 296). */
export const riskLevelGroupKey = 'risk_levels';

export const companyListSchema = pagedListSchema(companySchema);

/**
 * PPT-1 (mig 326) — GET/PUT /companies/{id}/program-types: the program types a PARTNER supports.
 *
 * One opaque field on purpose. `programTypeId` refers to definitions.definitions (group
 * `program_types`), which is tenant-editable and owned by Definitions — the LABEL is resolved from
 * that group, never copied onto this contract [STD-OWN]. Same identifier space as an event's
 * programTypeId, which is what makes partner scope and event scope joinable.
 */
export const partnerProgramTypeSchema = z.object({
  programTypeId: z.guid()
});
export const partnerProgramTypeListSchema = z.array(partnerProgramTypeSchema);
export type PartnerProgramType = z.infer<typeof partnerProgramTypeSchema>;

/**
 * PPT-BULK — GET /companies/{id}/partner-candidates: users the bulk-assign modal may act on, each
 * labelled with what assignment WOULD do.
 *
 * ⚠ `outcomeIfAssigned` IS SERVER-DECIDED. It is the skip rule (does this user already have a partner for
 * the requested type, and is it this company?) and deriving it in the browser would put a copy of that rule
 * in the client, where it could drift from the write that enforces it.
 *
 * Name PARTS travel separately from `displayName` because the modal shows them as columns; re-splitting the
 * composed name would guess at which token is a middle name, and USR-PARTS (mig 311) exists because that
 * guess was wrong. `countryDefinitionId` stays OPAQUE — Definitions owns the label [STD-OWN] and the admin
 * SPA already loads that catalogue.
 */
export const partnerCandidateOutcomes = ['would_assign', 'already_assigned', 'has_other_partner'] as const;

/**
 * USR-BULK-MULTI — one requested program type's state for one user. The row-level `outcomeIfAssigned`
 * is the AGGREGATE over the requested types (has_other_partner if ANY type is elsewhere,
 * already_assigned if EVERY type is here, else would_assign); this is the per-type truth.
 */
export const partnerCandidateTypeSchema = z.object({
  programTypeId: z.guid(),
  currentCompanyId: z.guid().nullish(),
  currentCompanyName: z.string().nullish(),
  outcome: z.enum(partnerCandidateOutcomes)
});
export type PartnerCandidateType = z.infer<typeof partnerCandidateTypeSchema>;

export const partnerCandidateSchema = z.object({
  userId: z.guid(),
  firstName: z.string().nullish(),
  middleName: z.string().nullish(),
  lastName: z.string().nullish(),
  displayName: z.string(),
  email: z.string().nullish(),
  username: z.string().nullish(),
  roles: z.array(z.string()).nullish().transform((v) => v ?? []),
  countryDefinitionId: z.guid().nullish(),
  schoolId: z.guid().nullish(),
  schoolName: z.string().nullish(),
  /** The partner this user has for the FIRST requested type (null = none) — read `types` instead. */
  currentCompanyId: z.guid().nullish(),
  currentCompanyName: z.string().nullish(),
  /** AGGREGATE over the requested types — see `partnerCandidateTypeSchema`. */
  outcomeIfAssigned: z.enum(partnerCandidateOutcomes),
  /** One entry per requested type, in request order. */
  types: z.array(partnerCandidateTypeSchema).nullish().transform((v) => v ?? [])
});

export const partnerCandidateListSchema = z.object({
  items: z.array(partnerCandidateSchema).nullish().transform((v) => v ?? []),
  totalItems: z.number().int(),
  /**
   * Counts over the WHOLE filtered set, not the page — the modal warns about skips on pages the admin has
   * not looked at, which a page-scoped count could not do.
   */
  wouldAssignCount: z.number().int(),
  alreadyAssignedCount: z.number().int(),
  hasOtherPartnerCount: z.number().int()
});
export type PartnerCandidate = z.infer<typeof partnerCandidateSchema>;
export type PartnerCandidateList = z.infer<typeof partnerCandidateListSchema>;

/**
 * PPT-BULK — GET /companies/{id}/partner-candidates/ids: every user id matching the filter, so
 * "select all N matching" means what it says rather than "select this page".
 *
 * `truncated` is the honest half: above the server's ceiling the client is NOT holding every match, and a UI
 * that ignored it would claim to have selected all of them. `totalItems` is the real total either way, so
 * the screen can ask the admin to narrow the filter instead of quietly doing less than it says.
 */
export const partnerCandidateIdsSchema = z.object({
  userIds: z.array(z.guid()).nullish().transform((v) => v ?? []),
  totalItems: z.number().int(),
  truncated: z.boolean()
});
export type PartnerCandidateIds = z.infer<typeof partnerCandidateIdsSchema>;

// ==============================|| USR-BULK — bulk salesperson / country / school on the user list ||============================== //

/**
 * USR-BULK — GET /users/ids: every user id the admin user list's CURRENT filters return (same query
 * parameters as GET /users). Called when a "select all N matching" action RUNS, not when it is ticked, so
 * the population is the one at confirm time.
 *
 * ⚠ `truncated` IS THE HONEST HALF (same contract as `partnerCandidateIdsSchema`): above the server's ceiling
 * the client does not hold every match, and the dialog must say so rather than quietly do less.
 */
export const userIdsSchema = z.object({
  userIds: z.array(z.guid()).nullish().transform((v) => v ?? []),
  totalItems: z.number().int(),
  truncated: z.boolean()
});
export type UserIds = z.infer<typeof userIdsSchema>;

/** The three bulk writes, one endpoint each: POST /users/bulk/{kind}. */
export const userBulkKinds = ['salesperson', 'country', 'school'] as const;
export type UserBulkKind = (typeof userBulkKinds)[number];

/**
 * Request body for POST /users/bulk/{kind}. `valueId` is the salesperson's user id, the country definition
 * id or the school id; null CLEARS (salesperson only — the server refuses null for the other two).
 * `onlyEmpty`: users who already hold a value are skipped (`reason: 'has_value'`) instead of overwritten.
 */
export interface UserBulkUpdateRequest {
  userIds: string[];
  valueId: string | null;
  onlyEmpty: boolean;
}

/**
 * One row per requested user.
 *
 * ⚠ `outcome` AND `reason` ARE PLAIN STRINGS, NOT `z.enum`. Skip reasons grow with the backend (a new guard
 * is a new reason), and one unknown value would reject the WHOLE response — the run happened, the admin just
 * could not see it. The dialog labels the codes it knows and falls back to the code for the rest.
 */
export const userBulkRowSchema = z.object({
  userId: z.guid(),
  /** From the server — after "select all matching" the client never rendered most of these users. */
  displayName: z.string().nullish(),
  /** `updated` | `unchanged` | `skipped` */
  outcome: z.string(),
  /** e.g. `has_value`, `merging`, `merged`, `not_found`, `school_outside_country` */
  reason: z.string().nullish(),
  previousValueId: z.guid().nullish(),
  previousValueName: z.string().nullish(),
  /** Country runs: the user's school was not in the new country and was CLEARED (product, 2026-11-04). */
  schoolCleared: z.boolean().nullish().transform((v) => v ?? false),
  clearedSchoolName: z.string().nullish()
});
export type UserBulkRow = z.infer<typeof userBulkRowSchema>;

export const userBulkResultSchema = z.object({
  kind: z.string(),
  valueId: z.guid().nullish(),
  rows: z.array(userBulkRowSchema).nullish().transform((v) => v ?? []),
  updatedCount: z.number().int(),
  unchangedCount: z.number().int(),
  skippedCount: z.number().int(),
  /** Country runs: how many updated users also lost their school. */
  schoolClearedCount: z.number().int().nullish().transform((v) => v ?? 0)
});
export type UserBulkResult = z.infer<typeof userBulkResultSchema>;

/**
 * PPT-BULK — POST /companies/{id}/partner-selections/bulk result. ROW-BY-ROW, because a bulk action's
 * outcome is per row and a single status would have to lie about at least one of them (the
 * InviteStudentsToEventCommand precedent). `previousCompanyName` rides along so the UI can name what was
 * displaced — or what a skip left alone — without a request per row.
 */
export const partnerAssignOutcomes = ['assigned', 'replaced', 'unchanged', 'skipped'] as const;
export const partnerAssignSkipReasons = ['has_other_partner', 'not_found'] as const;

export const partnerAssignRowSchema = z.object({
  userId: z.guid(),
  /** USR-BULK-MULTI: one row per (user, type) — this is the type the row is about. */
  programTypeId: z.guid(),
  /**
   * Who the row is about, from the SERVER.
   *
   * ⚠ NOT RESOLVED CLIENT-SIDE, and that is the point. The modal used to name result rows out of the
   * candidate page it had loaded, which only works while the selection fits on one page — after "select
   * all matching" a 250-user run reports on ids the client never rendered, and every one of those rows
   * printed a bare GUID. Null only for a `not_found` row: there is no user to name.
   */
  displayName: z.string().nullish(),
  outcome: z.enum(partnerAssignOutcomes),
  reason: z.enum(partnerAssignSkipReasons).nullish(),
  previousCompanyId: z.guid().nullish(),
  previousCompanyName: z.string().nullish()
});

export const partnerBulkAssignResultSchema = z.object({
  companyId: z.guid(),
  /** The FIRST requested type — kept for single-type readers. */
  programTypeId: z.guid(),
  programTypeIds: z.array(z.guid()).nullish().transform((v) => v ?? []),
  rows: z.array(partnerAssignRowSchema).nullish().transform((v) => v ?? []),
  /** These four count (user, type) PAIRS, not users. */
  assignedCount: z.number().int(),
  replacedCount: z.number().int(),
  unchangedCount: z.number().int(),
  skippedCount: z.number().int()
});
export type PartnerAssignRow = z.infer<typeof partnerAssignRowSchema>;
export type PartnerBulkAssignResult = z.infer<typeof partnerBulkAssignResultSchema>;

/** Body for the bulk write. `replaceExisting` defaults false — the destructive direction is asked for. */
export type BulkAssignPartnerSelectionsRequest = {
  /** USR-BULK-MULTI: one or more types, distinct. (The server still accepts a legacy single `programTypeId`.) */
  programTypeIds: string[];
  userIds: string[];
  replaceExisting?: boolean;
};

/**
 * PPT-2 — GET /users/{id}/partner-selections: a user's partner PER PROGRAM TYPE (admin view; the
 * self equivalent is /users/me/partner-selections).
 *
 * ⚠ This — not `users.company_id` — is what decides which partner employees can see the user. The
 * admin "Partner" (employer) field still writes that column, but since mig 327 it only carries the
 * partner-EMPLOYEE employment link; for a student it changes nothing about visibility.
 */
export const userPartnerSelectionSchema = z.object({
  programTypeId: z.guid(),
  companyId: z.guid(),
  companyName: z.string()
});
export const userPartnerSelectionListSchema = z.array(userPartnerSelectionSchema);
export type UserPartnerSelection = z.infer<typeof userPartnerSelectionSchema>;

/**
 * SP-TERR (mig 560) — GET/PUT /users/{id}/salesperson-countries: the countries (opaque definition ids)
 * in one salesperson's territory. A country has at most one salesperson; PUT replaces the whole set.
 */
export const salespersonCountryIdsSchema = z.array(z.guid());

/**
 * SP-TERR — GET /users/salesperson-territories: every assigned country and its CURRENT salesperson (an
 * owner who lost the salesperson role is left out). Drives the picker's "taken by" and the admin
 * form's country → salesperson default.
 */
export const salespersonTerritorySchema = z.object({
  countryDefinitionId: z.guid(),
  userId: z.guid(),
  userName: z.string()
});
export const salespersonTerritoryListSchema = z.array(salespersonTerritorySchema);
export type SalespersonTerritory = z.infer<typeof salespersonTerritorySchema>;

/**
 * PPT-SCH (mig 329) — GET/PUT /schools/{id}/partner-selections: which partner a SCHOOL routes each
 * program type to, and therefore the DEFAULT its students inherit.
 *
 * ⚠ A DEFAULT, NOT A RULE. Applying it fills in only the types a student has not chosen for; their
 * own selection stays authoritative. Re-routing a school does NOT move students already attached.
 */
/**
 * PPT-SBULK — a row of the ATANAN OKULLAR list: a school routing at least one program type through this
 * partner.
 *
 * ⚠ A school appears ONCE with its types as a list — the server groups. Flattening to one row per
 * selection would make the tab's count disagree with how many schools the admin thinks they have.
 */
export const companyAssignedSchoolSchema = z.object({
  schoolId: z.guid(),
  legalName: z.string(),
  /** Opaque `definitions` id — Identity cannot read that schema, so the FE resolves the label. */
  countryDefinitionId: z.guid().nullish(),
  status: z.string(),
  programTypeIds: z.array(z.guid()).nullish().transform((v) => v ?? []),
  /** People under this school — what a cascade would reach. Not a count of selections. */
  memberCount: z.number().int()
});

/**
 * ⚠ `countsByProgramType` counts SELECTIONS, not schools, so its values sum HIGHER than `totalItems`
 * when a school routes several types here. Same caveat as the assigned-USERS list; do not "correct" it.
 */
export const companyAssignedSchoolListSchema = z.object({
  items: z.array(companyAssignedSchoolSchema).nullish().transform((v) => v ?? []),
  totalItems: z.number().int(),
  countsByProgramType: z.record(z.string(), z.number().int()).nullish().transform((v) => v ?? {})
});
export type CompanyAssignedSchool = z.infer<typeof companyAssignedSchoolSchema>;

/**
 * PPT-SBULK — the SCHOOL mirror of the partner-candidate contracts.
 *
 * GET /companies/{id}/school-candidates: schools this partner may be assigned for one program type,
 * each labelled with what assignment WOULD do and — because the write can cascade — how many PEOPLE it
 * would move.
 *
 * ⚠ THE OUTCOME AND THE COUNTS COME FROM THE SERVER, and re-deriving either here would be the PPT-BULK
 * mistake in a new costume: `outcomeIfAssigned` IS the skip rule, and `affectedUserCount` is what stands
 * between an admin and moving people they never reviewed.
 */
export const partnerSchoolCandidateOutcomes = ['would_assign', 'already_assigned', 'has_other_partner'] as const;

/**
 * SCH-BULK-MULTI — one requested program type's state for one school. The row-level
 * `outcomeIfAssigned` is the AGGREGATE over the requested types (has_other_partner if ANY type is
 * elsewhere, already_assigned if EVERY type is here, else would_assign); this is the per-type truth.
 */
export const partnerSchoolCandidateTypeSchema = z.object({
  programTypeId: z.guid(),
  currentCompanyId: z.guid().nullish(),
  currentCompanyName: z.string().nullish(),
  outcome: z.enum(partnerSchoolCandidateOutcomes),
  affectedUserCount: z.number().int()
});
export type PartnerSchoolCandidateType = z.infer<typeof partnerSchoolCandidateTypeSchema>;

export const partnerSchoolCandidateSchema = z.object({
  schoolId: z.guid(),
  legalName: z.string(),
  /** OPAQUE definitions id (group 'country') — the label is resolved from the catalogue [STD-OWN]. */
  countryDefinitionId: z.guid().nullish(),
  /** The partner this school routes the FIRST requested type to (null = none) — read `types` instead. */
  currentCompanyId: z.guid().nullish(),
  currentCompanyName: z.string().nullish(),
  /** AGGREGATE over the requested types — see `partnerSchoolCandidateTypeSchema`. */
  outcomeIfAssigned: z.enum(partnerSchoolCandidateOutcomes),
  /** People under this school (primary school, plus membership-only users who have none). */
  memberCount: z.number().int(),
  /**
   * Of those, how many the cascade would actually MOVE — i.e. whose selection for this type is not
   * already this company. Show THIS beside the school, never `memberCount`: the difference is the
   * people who would not change, and promising a bigger change than happens is how a confirmation
   * stops being read. Over several types: people moved for AT LEAST ONE of them, each counted once.
   */
  affectedUserCount: z.number().int(),
  /** One entry per requested type, in request order. */
  types: z.array(partnerSchoolCandidateTypeSchema).nullish().transform((v) => v ?? [])
});

export const partnerSchoolCandidateListSchema = z.object({
  items: z.array(partnerSchoolCandidateSchema).nullish().transform((v) => v ?? []),
  totalItems: z.number().int(),
  /** Breakdown over the WHOLE filter, so the modal can say what `outcome=` hid. */
  wouldAssignCount: z.number().int(),
  alreadyAssignedCount: z.number().int(),
  hasOtherPartnerCount: z.number().int(),
  /**
   * DISTINCT people the cascade would move.
   *
   * ⚠ NEVER SUM THE ROWS' `affectedUserCount` INSTEAD. A member with no primary school can sit under
   * several matching schools and appears in each one's count; the write moves them once. A summed figure
   * overstates, and overstating is the one direction that teaches an admin to click through the warning.
   *
   * ⚠ Unlike the three counts above, this one FOLLOWS the outcome filter — deliberately. Those describe
   * the whole filter; this must equal what a cascade would actually do, and "select all matching"
   * resolves through the ids endpoint, which applies the same outcome filter.
   */
  totalAffectedUserCount: z.number().int()
});
export type PartnerSchoolCandidate = z.infer<typeof partnerSchoolCandidateSchema>;
export type PartnerSchoolCandidateList = z.infer<typeof partnerSchoolCandidateListSchema>;

/** GET /companies/{id}/school-candidates/ids — every matching school id, so "select all" is not a lie. */
export const partnerSchoolCandidateIdsSchema = z.object({
  schoolIds: z.array(z.guid()).nullish().transform((v) => v ?? []),
  totalItems: z.number().int(),
  truncated: z.boolean()
});
export type PartnerSchoolCandidateIds = z.infer<typeof partnerSchoolCandidateIdsSchema>;

/** POST /companies/{id}/school-partner-selections/bulk — result, row by row. */
export const schoolPartnerAssignOutcomes = ['assigned', 'replaced', 'unchanged', 'skipped'] as const;
export const schoolPartnerAssignSkipReasons = ['has_other_partner', 'not_found'] as const;

export const schoolPartnerAssignRowSchema = z.object({
  schoolId: z.guid(),
  /** SCH-BULK-MULTI: one row per (school, type) — this is the type the row is about. */
  programTypeId: z.guid(),
  /**
   * From the SERVER. After "select all matching" the run reports on ids the client never rendered, so a
   * page-side lookup prints raw GUIDs on the one screen whose job is to say what changed. Null only for
   * a `not_found` row — there is no school left to name.
   */
  legalName: z.string().nullish(),
  outcome: z.enum(schoolPartnerAssignOutcomes),
  reason: z.enum(schoolPartnerAssignSkipReasons).nullish(),
  previousCompanyId: z.guid().nullish(),
  previousCompanyName: z.string().nullish(),
  /**
   * People moved under this school. 0 when the cascade was not requested — and legitimately non-zero on
   * an `unchanged` row, because a school already routed here says nothing about what its people hold.
   */
  cascadedUserCount: z.number().int().nullish().transform((v) => v ?? 0)
});

export const schoolPartnerBulkAssignResultSchema = z.object({
  companyId: z.guid(),
  /** The FIRST requested type — kept for single-type readers. */
  programTypeId: z.guid(),
  programTypeIds: z.array(z.guid()).nullish().transform((v) => v ?? []),
  rows: z.array(schoolPartnerAssignRowSchema).nullish().transform((v) => v ?? []),
  /** These four count (school, type) PAIRS, not schools. */
  assignedCount: z.number().int(),
  replacedCount: z.number().int(),
  unchangedCount: z.number().int(),
  skippedCount: z.number().int(),
  /** DISTINCT people moved by the run — not the sum of the rows. See the list schema. */
  cascadedUserCount: z.number().int(),
  /** (person, type) selections rewritten — one audit event each. Equals the people count for one type. */
  cascadedSelectionCount: z.number().int().nullish().transform((v) => v ?? 0),
  /**
   * The run's correlation id. Show it: it is the only handle on "what did that cascade overwrite" —
   * every emitted event carries the previous company and this id, so a revert is a targeted script over
   * the audit trail. Without a line of explanation next to it, it reads as debug noise and nobody keeps
   * it, which defeats the point.
   */
  correlationId: z.string()
});
export type SchoolPartnerAssignRow = z.infer<typeof schoolPartnerAssignRowSchema>;
export type SchoolPartnerBulkAssignResult = z.infer<typeof schoolPartnerBulkAssignResultSchema>;

/**
 * Body for the school bulk write. BOTH flags default false and are SEPARATE on purpose:
 * `replaceExisting` moves SCHOOLS already routed elsewhere, `cascadeToMembers` moves those schools'
 * PEOPLE. Neither implies the other — an admin fixing a routing must be able to leave people alone.
 */
export type BulkAssignSchoolPartnerSelectionsRequest = {
  /** SCH-BULK-MULTI: one or more types, distinct. (The server still accepts a legacy single `programTypeId`.) */
  programTypeIds: string[];
  schoolIds: string[];
  replaceExisting?: boolean;
  cascadeToMembers?: boolean;
};

// =============================================================================
// SCH-UNASSIGN — POST /companies/{id}/school-partner-selections/bulk-remove.
// The mirror of the bulk assign, with its own outcomes because removal can fail
// in ways assignment cannot.
// =============================================================================

export const schoolPartnerRemoveOutcomes = ['removed', 'skipped'] as const;

/**
 * ⚠ `routes_elsewhere` IS NOT AN ERROR. The admin acted from one partner's page; a school that has
 * since moved to a competitor is reported with that competitor's name, never stripped. Render it as a
 * statement about the school, not as a failure the admin has to fix.
 */
export const schoolPartnerRemoveSkipReasons = ['not_found', 'not_routed', 'routes_elsewhere'] as const;

export const schoolPartnerRemoveRowSchema = z.object({
  schoolId: z.guid(),
  /** From the SERVER, for the reason the assign row's does. Null only for a `not_found` row. */
  legalName: z.string().nullish(),
  outcome: z.enum(schoolPartnerRemoveOutcomes),
  reason: z.enum(schoolPartnerRemoveSkipReasons).nullish(),
  /** For a `routes_elsewhere` row: the partner it actually routes to, so the report can name it. */
  currentCompanyId: z.guid().nullish(),
  currentCompanyName: z.string().nullish(),
  /** People whose inherited partner was cleared under this school. 0 when not requested. */
  clearedUserCount: z.number().int().nullish().transform((v) => v ?? 0)
});

export const schoolPartnerBulkRemoveResultSchema = z.object({
  companyId: z.guid(),
  programTypeId: z.guid(),
  rows: z.array(schoolPartnerRemoveRowSchema).nullish().transform((v) => v ?? []),
  removedCount: z.number().int(),
  skippedCount: z.number().int(),
  /** DISTINCT people cleared by the run — not the sum of the rows. Same trap as the assign result. */
  clearedUserCount: z.number().int(),
  /**
   * ⚠ WORTH MORE HERE THAN ON AN ASSIGN. Every cleared row is GONE, so the audit trail is the only
   * place the previous partner still exists — without this id a revert is a guess bounded by timestamps.
   */
  correlationId: z.string()
});
export type SchoolPartnerRemoveRow = z.infer<typeof schoolPartnerRemoveRowSchema>;
export type SchoolPartnerBulkRemoveResult = z.infer<typeof schoolPartnerBulkRemoveResultSchema>;

/**
 * Body for the school bulk REMOVE.
 *
 * ⚠ THERE IS NO `replaceExisting` TWIN, deliberately: removal never touches a routing pointing
 * somewhere else, so there is no destructive direction to opt into at the SCHOOL level. The one flag is
 * about PEOPLE — `clearMembers` also drops the partner those schools' teachers and students inherited,
 * which is the half that deleting the school's row does NOT undo.
 */
export type BulkRemoveSchoolPartnerSelectionsRequest = {
  programTypeId: string;
  schoolIds: string[];
  clearMembers?: boolean;
};

export const schoolPartnerSelectionSchema = z.object({
  programTypeId: z.guid(),
  companyId: z.guid(),
  companyName: z.string()
});
export const schoolPartnerSelectionListSchema = z.array(schoolPartnerSelectionSchema);
export type SchoolPartnerSelectionDto = z.infer<typeof schoolPartnerSelectionSchema>;

/**
 * PPT-1 — one entry of the 422 `blockedProgramTypes` extension returned when a set-replace tries to
 * remove a type students still hold a selection for.
 *
 * ⚠ THE COUNT IS THE POINT. There is no foreign key behind this rule (half of it would have pointed
 * across schemas), so the server counts instead — which is also the only reason the form can say
 * WHICH types are locked and by HOW MANY students. A plain 409 could not carry that.
 */
export const blockedProgramTypeSchema = z.object({
  programTypeId: z.guid(),
  selectionCount: z.number()
});
export type BlockedProgramType = z.infer<typeof blockedProgramTypeSchema>;

/** RoleUserCountDto — GET /companies/{id}/user-role-counts (detail-page tab labels; 0-count included). */
export const companyUserRoleCountSchema = z.object({
  roleCode: z.string(),
  roleName: z.string(),
  count: z.number()
});
export const companyUserRoleCountListSchema = z.array(companyUserRoleCountSchema);
export type CompanyUserRoleCount = z.infer<typeof companyUserRoleCountSchema>;

/** POST/PUT /api/v1/companies body — Status optional on create (backend defaults active). */
export interface CompanyRequest {
  legalName: string;
  tradingName?: string | null;
  /** Required on create ('partner' | 'organizer'); ignored on update (immutable). */
  type?: CompanyType;
  registrationNumber?: string | null;
  taxId?: string | null;
  email?: string | null;
  phone?: string | null;
  websiteUrl?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  stateRegion?: string | null;
  postalCode?: string | null;
  /** DG-1: geography by definition id (opaque refs to definitions.definitions.id). */
  countryDefinitionId?: string | null;
  cityDefinitionId?: string | null;
  districtDefinitionId?: string | null;
  /** CO-C1: primary contact person (all optional). */
  contactPersonName?: string | null;
  contactPersonTitle?: string | null;
  contactPersonEmail?: string | null;
  contactPersonPhone?: string | null;
  status?: CompanyStatus;
  /** CO-salesperson: the salesperson accountable for this company (any live user). null = none. */
  salespersonId?: string | null;
  /** CO-H1: nullable parent company (self-reference). null/omitted = top-level. */
  parentCompanyId?: string | null;
  /** CTERM: opaque definitions ref (group risk_levels). null = not yet assessed, NOT the lowest band. */
  riskLevelDefinitionId?: string | null;
  /**
   * CTERM: agreed commission in whole units. null = nothing agreed; 0 = agreed AND zero — send the
   * distinction, do not coalesce an empty field to 0. Percent or flat amount per {@link isPercentage}.
   */
  commission?: number | null;
  /** CTERM: agreed incentive in whole units. Same null-vs-0 rule as {@link commission}. */
  incentive?: number | null;
  /**
   * CTERM: the UNIT of BOTH commission and incentive — true = percent (each capped at 100 by the
   * server and the DB CHECK), false = flat amount. Omitted defaults to percent.
   */
  isPercentage?: boolean;
}

export type Company = z.infer<typeof companySchema>;

// =============================================================================
// SCH — School (identity.schools). A first-class entity duplicated from Company
// (no `type`), PLUS partner_id → a 'partner' company. Mirrors SchoolDto field-for-
// field; hierarchy via parentSchoolId; partnerName resolved on the detail response.
// =============================================================================

/** SchoolDto — the full school profile (Company mirror, no type, + partner). */
export const schoolSchema = z.object({
  id: z.guid(),
  legalName: z.string(),
  tradingName: z.string().nullish(),
  registrationNumber: z.string().nullish(),
  taxId: z.string().nullish(),
  // SCH-TYPE (mig 412): the school's type — an opaque definitions.definitions.id in group
  // 'school_type'. `nullish`, not required: the group ships EMPTY, so most schools have none yet,
  // and a required field here would fail to parse the whole LIST rather than blank one cell.
  schoolTypeDefinitionId: z.guid().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  websiteUrl: z.string().nullish(),
  addressLine1: z.string().nullish(),
  addressLine2: z.string().nullish(),
  stateRegion: z.string().nullish(),
  postalCode: z.string().nullish(),
  // DG-1: geography by definition id (opaque refs to definitions.definitions.id).
  countryDefinitionId: z.guid().nullish(),
  cityDefinitionId: z.guid().nullish(),
  districtDefinitionId: z.guid().nullish(),
  // CO-C1: primary contact person (single embedded contact; all optional).
  contactPersonName: z.string().nullish(),
  contactPersonTitle: z.string().nullish(),
  contactPersonEmail: z.string().nullish(),
  contactPersonPhone: z.string().nullish(),
  // SIGNUP-SCHOOL-NEW: schoolStatuses, NOT companyStatuses — a school can be 'pending'. Parsing this
  // with the company list is what would break the admin list page on the first proposed school.
  status: z.enum(schoolStatuses),
  // PPT-SCH (mig 330): partnerId is GONE — a school's partner is PER PROGRAM TYPE
  // (schoolPartnerSelectionSchema). partnerName stays nullish for one release so a response from an
  // older backend still parses; it is always null now.
  partnerName: z.string().nullish(),
  // SCH-salesperson: the salesperson accountable for this school (ANY live user). Name resolved on detail
  // AND on the list (NAMES-SWEEP); null on command ACKs.
  salespersonId: z.guid().nullish(),
  salespersonName: z.string().nullish(),
  // Hierarchy: nullable parent school (self-reference). parentLegalName + childCount on detail only.
  parentSchoolId: z.guid().nullish(),
  parentLegalName: z.string().nullish(),
  childCount: z.number().default(0),
  createdAt: isoDate,
  updatedAt: isoDate,
  /**
   * SCH-LIST-PARTNER: this school's per-type partner routings, resolved by the LIST for the whole page
   * in one query. The admin list carries a column per program type and cannot ask
   * `/schools/{id}/partner-selections` once per row.
   *
   * ⚠ THE SERVER SENDS `null` ON EVERY OTHER SURFACE (the detail response and both command results),
   * meaning "not resolved here" rather than "routes nothing". Collapsed to `[]` on purpose: the only
   * consumer is the list, where the two readings coincide, and a page that had to tell them apart would
   * be a page reading a routing off a response that never carried one.
   */
  partnerSelections: z
    .array(schoolPartnerSelectionSchema)
    .nullish()
    .transform((v) => v ?? [])
});

export const schoolListSchema = pagedListSchema(schoolSchema);

/** School detail role-count rows — same shape as the company variant. */
export const schoolUserRoleCountListSchema = companyUserRoleCountListSchema;

/** POST/PUT /api/v1/schools body — Status optional on create (backend defaults active). */
export interface SchoolRequest {
  legalName: string;
  tradingName?: string | null;
  registrationNumber?: string | null;
  taxId?: string | null;
  /** SCH-TYPE (mig 412): opaque definitions.definitions.id in group 'school_type'. null = unset. */
  schoolTypeDefinitionId?: string | null;
  email?: string | null;
  phone?: string | null;
  websiteUrl?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  stateRegion?: string | null;
  postalCode?: string | null;
  /** DG-1: geography by definition id (opaque refs to definitions.definitions.id). */
  countryDefinitionId?: string | null;
  cityDefinitionId?: string | null;
  districtDefinitionId?: string | null;
  /** CO-C1: primary contact person (all optional). */
  contactPersonName?: string | null;
  contactPersonTitle?: string | null;
  contactPersonEmail?: string | null;
  contactPersonPhone?: string | null;
  status?: SchoolStatus;
  /** SCH-salesperson: the salesperson accountable for this school (any live user). null = none. */
  salespersonId?: string | null;
  /** Hierarchy: nullable parent school (self-reference). null/omitted = top-level. */
  parentSchoolId?: string | null;
}

export type School = z.infer<typeof schoolSchema>;

// =============================================================================
// P1-3a/b/c — Account Center (Identity :5101). Mirror the DTOs field-by-field;
// secret/otpauth/backup-code plaintext appear ONLY in enroll/verify responses.
// =============================================================================

/** MyProfileDto — PUT /users/me yanıtı (P1-3a; phone S-12.b). */
export const myProfileSchema = z.object({
  id: z.guid(),
  /**
   * ⚠ NULLISH since STU-HANDLE (mig 314): a teacher-created student has no address until they add one, and a
   * required string here throws on their profile screen — the one screen that would let them fix it. Read
   * `emailVerified` (and MeResult.hasEmail) rather than inferring anything from a null.
   */
  email: z.string().nullish(),
  /** The COMPOSED name (Identity's generated full_name coalesced to the e-mail) — render this, never edit it. */
  displayName: z.string(),
  // USR-PARTS (mig 311): the name PARTS, for a form to bind to.
  //
  // ⚠ Present when this schema reads GET /users/me — that endpoint returns the richer MeResult and zod
  // strips the rest — but ABSENT from the PUT /users/me response (MyProfileDto has no parts). Hence
  // nullish, and hence a caller must not re-seed its form from the PUT response, or it would blank the
  // fields the user just filled in.
  firstName: z.string().nullish(),
  middleName: z.string().nullish(),
  lastName: z.string().nullish(),
  avatarUrl: z.string().nullish(),
  phone: z.string().nullish(),
  phoneVerified: z.boolean(),
  status: z.enum(userWireStatuses),
  /**
   * ⚠ NULLISH, and not by preference. This schema is parsed against TWO different server shapes:
   * MyProfileDto (the PUT /users/me response, which HAS updatedAt) and MeResult (GET /users/me, which does
   * NOT). Requiring it made `myProfileSchema.parse()` THROW on every GET — so the student Account Center's
   * whole info section rendered blank, e-mail included, because one failed query blanks every field seeded
   * from it. Pre-existing; found while verifying USR-PARTS.
   */
  updatedAt: isoDate.nullish(),
  /**
   * STU-HANDLE (mig 314): has the address above been proven? Required on the server's write response, but
   * NULLISH here for the same reason `updatedAt` is — this schema is also parsed against MeResult, and a
   * required field would throw on GET. A missing value means "don't nag", never "unverified".
   */
  emailVerified: z.boolean().nullish()
});

/** Body for PUT /users/me/email (STU-HANDLE, mig 314). Blank is refused server-side — it does not clear. */
export interface SetMyEmailRequest {
  email: string;
}

/**
 * UF — UserProfileDto — GET /users/me/profile (self) + the embedded `profile` on the admin
 * user-detail. Demographics (country/birthDate/gender) + the UF-1 extended attributes. All fields
 * optional; a null field clears it on PUT. Ids are opaque Definitions refs (never resolved on the
 * wire) and `teacherUserId` is an opaque Identity user id. NOTE: the mailing-address fields were
 * REMOVED here (address moved to the dedicated /addresses CRUD — see userAddressSchema).
 * PX: `passportNumber` is READ-ONLY here and no longer writable through this shape — the passport
 * moved to its own sensitive sub-resource (see userPassportSchema). The server only fills it in for
 * (a) the caller's own /users/me and (b) an admin holding `identity.users.sensitive_view`; everyone
 * else sees null, so never treat its presence as "the user has no passport".
 */
export const userExtendedProfileSchema = z.object({
  countryDefinitionId: z.guid().nullish(),
  birthDate: z.string().nullish(),
  genderDefinitionId: z.guid().nullish(),
  parentGuardianEmail: z.string().nullish(),
  parentGuardianPhone: z.string().nullish(),
  passportNumber: z.string().nullish(),
  // PX: opaque Definitions refs — groups `shirt_sizes` / `meal_preferences` (migration 254).
  shirtSizeDefinitionId: z.guid().nullish(),
  mealPreferenceDefinitionId: z.guid().nullish(),
  preferredLanguageDefinitionId: z.guid().nullish(),
  teacherUserId: z.guid().nullish(),
  // AGECAT-PT (mig 472): the user's age band PER PROGRAM TYPE, replacing the englishAge…/mathAge… pair.
  // The server always sends an array (empty when unplaced); the nullish+transform is the same defensive
  // shape `options` uses on event questions, so a caller never has to guard for null.
  ageCategories: z.array(userAgeCategorySchema).nullish().transform((v) => v ?? []),
  // SAC: false ⇒ every placement is derived from birthDate on the client (stored rows ignored); true ⇒
  // the admin-set manual rows are authoritative. Still ONE flag for the student, not one per programme.
  // Admin-only write; read here for display + to drive the derived/manual branch.
  specialAgeCategory: z.boolean().nullish(),
  // UF-3 admin-assigned single references. schoolId → identity.schools (SCH split).
  schoolId: z.guid().nullish(),
  teeneagleParentUserId: z.guid().nullish()
});

/** Alias kept for the self-service Account Center callers (identical shape). */
export const userDemographicsSchema = userExtendedProfileSchema;

/**
 * PROF: minimal projection of GET /api/v1/users/me for the event "profile field" auto-fill — the
 * caller's own name/phone plus the embedded demographic `profile` (MeResult.Profile, null until saved).
 * Extra MeResult fields are ignored by the object parse.
 */
export const meWithProfileSchema = z.object({
  displayName: z.string(),
  phone: z.string().nullish(),
  profile: userExtendedProfileSchema.nullish()
});
export type MeWithProfile = z.infer<typeof meWithProfileSchema>;

/**
 * INV-SEED-CO: GET /api/v1/users/me/employer — the caller's company (users.company_id) as invoice
 * details; `data: null` when there is none, or it is deleted/inactive. Geography is OPAQUE definition
 * ids, resolved by the caller. No tax office: the company record does not hold one.
 */
export const employerInvoiceDetailsSchema = z.object({
  companyId: z.guid(),
  legalName: z.string(),
  taxId: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  addressLine1: z.string().nullish(),
  addressLine2: z.string().nullish(),
  postalCode: z.string().nullish(),
  countryDefinitionId: z.guid().nullish(),
  cityDefinitionId: z.guid().nullish(),
  districtDefinitionId: z.guid().nullish(),
  contactPersonName: z.string().nullish(),
  contactPersonTitle: z.string().nullish(),
  contactPersonEmail: z.string().nullish(),
  contactPersonPhone: z.string().nullish()
});
export type EmployerInvoiceDetails = z.infer<typeof employerInvoiceDetailsSchema>;

export type UserExtendedProfile = z.infer<typeof userExtendedProfileSchema>;

/**
 * UpdateMyProfileDemographicsRequest — PUT /users/me/profile body (self). SAC: the client sends the
 * EFFECTIVE age categories (derived from birthDate when not special, or the preserved manual ids when
 * special) so the stored columns stay populated; the specialAgeCategory FLAG itself is admin-only.
 * PX: `passportNumber` is deliberately ABSENT — write it through PUT /users/{me|:id}/passport.
 */
export interface UserDemographicsRequest {
  countryDefinitionId?: string | null;
  birthDate?: string | null;
  genderDefinitionId?: string | null;
  parentGuardianEmail?: string | null;
  parentGuardianPhone?: string | null;
  shirtSizeDefinitionId?: string | null;
  mealPreferenceDefinitionId?: string | null;
  preferredLanguageDefinitionId?: string | null;
  teacherUserId?: string | null;
  /**
   * AGECAT-PT: the effective placements, one entry per program type.
   *
   * ⚠ OMITTING THIS IS NOT THE SAME AS SENDING []. Every other field here is null-through (absent or
   * null CLEARS the attribute); this one is not. Absent means "leave the stored placements alone",
   * `[]` means "clear them". The asymmetry is deliberate and matches the server
   * (UserAgeCategoryStore): a screen saving one unrelated field must not silently wipe a set it never
   * rendered, because unlike a phone number the user cannot see it to retype it.
   */
  ageCategories?: UserAgeCategory[] | null;
}

/**
 * UF-2 — UserAddressDto — a user's contact address (self + admin surfaces). Geography is by opaque
 * definition id. `isPrimary`: at most one per user; setting it true clears the others server-side.
 */
export const userAddressSchema = z.object({
  id: z.guid(),
  label: z.string().nullish(),
  isPrimary: z.boolean(),
  addressLine1: z.string().nullish(),
  addressLine2: z.string().nullish(),
  stateRegion: z.string().nullish(),
  postalCode: z.string().nullish(),
  countryDefinitionId: z.guid().nullish(),
  cityDefinitionId: z.guid().nullish(),
  districtDefinitionId: z.guid().nullish()
});
export const userAddressListSchema = z.array(userAddressSchema);
export type UserAddress = z.infer<typeof userAddressSchema>;

/** POST/PUT /users/{me|:id}/addresses body — UserAddressInput. */
export interface UserAddressWriteRequest {
  label?: string | null;
  isPrimary: boolean;
  addressLine1?: string | null;
  addressLine2?: string | null;
  stateRegion?: string | null;
  postalCode?: string | null;
  countryDefinitionId?: string | null;
  cityDefinitionId?: string | null;
  districtDefinitionId?: string | null;
}

/**
 * PX (migs 256–258) — the SENSITIVE profile sub-resources. These live OFF the shared profile shape on
 * purpose: health data is GDPR Art. 9 special category and the passport is an identity document, so
 * both sit behind their own permissions. The server gate is: the data subject always reaches their own
 * record via /users/me/..., while staff need `identity.users.sensitive_view` (read) or
 * `identity.users.sensitive_update` (write) — plain users.view/update is NOT enough (403).
 *
 * Never log or cache these payloads outside the query client, and never render them on a list view.
 */

/** UserHealthInfoDto — GET/PUT /users/{me|:id}/health. All-null when nothing has been entered yet. */
export const userHealthInfoSchema = z.object({
  /** Opaque Definitions ref — group `disability_types`. `none` is an ANSWER; null means "never asked". */
  disabilityTypeDefinitionId: z.guid().nullish(),
  allergies: z.string().nullish(),
  medications: z.string().nullish(),
  updatedAtUtc: isoDate.nullish()
});
export type UserHealthInfo = z.infer<typeof userHealthInfoSchema>;

/** PUT /users/{me|:id}/health body. A null/omitted field clears that attribute. */
export interface UserHealthInfoRequest {
  disabilityTypeDefinitionId?: string | null;
  allergies?: string | null;
  medications?: string | null;
}

/**
 * UserPassportDto — GET/PUT /users/{me|:id}/passport. `hasPhoto` is a FLAG, not a URL: the photo is an
 * authorized blob fetched from /users/{me|:id}/passport/photo (use client.getBlob) and the underlying
 * storage key is never exposed.
 */
export const userPassportSchema = z.object({
  passportNumber: z.string().nullish(),
  /** DateOnly on the wire (yyyy-MM-dd). A past date is valid — an expired passport is still a fact. */
  validUntil: z.string().nullish(),
  /** Opaque Definitions ref — the `country` group. */
  originCountryDefinitionId: z.guid().nullish(),
  hasPhoto: z.boolean(),
  updatedAtUtc: isoDate.nullish()
});
export type UserPassport = z.infer<typeof userPassportSchema>;

/**
 * RPT — GET /users/passports?userIds=… (repeated param). One entry per user that HAS a passport row;
 * users WITHOUT one are OMITTED rather than returned blank, which is how a caller distinguishes
 * "never filled in" from "filled in but empty". Staff only: requires identity.users.sensitive_view,
 * so a caller without it gets 403 for the whole call — handle that as "no passport columns", not as a
 * page failure.
 *
 * The cap is server-side (200 ids) and returns 400 rather than truncating, so a caller paging a large
 * roster must chunk its requests rather than assume a silent partial answer.
 */
export const userPassportEntrySchema = z.object({
  userId: z.guid(),
  passport: userPassportSchema
});
export const userPassportEntryListSchema = z.array(userPassportEntrySchema);
export type UserPassportEntry = z.infer<typeof userPassportEntrySchema>;

/** Server-side cap on one bulk passport request (mirrored from SensitiveProfileRules). */
export const userPassportBatchLimit = 200;

/** PUT /users/{me|:id}/passport body — text fields only; the photo has its own endpoint. */
export interface UserPassportRequest {
  passportNumber?: string | null;
  validUntil?: string | null;
  originCountryDefinitionId?: string | null;
}

/**
 * UF-1 — UserDetailDto — GET /api/v1/users/{id} (admin). Core (incl. structured name parts) + the
 * 1:1 extended profile (null when the user has none) + the user's contact addresses.
 */
export const userDetailSchema = z.object({
  id: z.guid(),
  /** ⚠ NULLISH since STU-HANDLE (mig 314) — same reason as userSummarySchema: an admin opening a
   *  teacher-created student's detail drawer would otherwise get a parse throw instead of the record. */
  email: z.string().nullish(),
  /**
   * USR-NAME (mig 310) / STU-HANDLE (mig 314): the second login identifier. UserDetailDto has always carried
   * it; the schema stripped it, so the detail drawer could not answer how a student with a handle and no
   * e-mail signs in — the only identifier such an account has.
   */
  username: z.string().nullish(),
  /** USER-CODE (mig 629): the 9-character user code, e.g. TE2700251. Null until the tenant saves a prefix. */
  userCode: z.string().nullish(),
  /** USER-MERGE (mig 643): set when status is 'merged' — the user this account was merged into. */
  mergedIntoUserId: z.guid().nullish(),
  displayName: z.string(),
  firstName: z.string().nullish(),
  middleName: z.string().nullish(),
  lastName: z.string().nullish(),
  phone: z.string().nullish(),
  status: z.enum(userWireStatuses),
  companyId: z.guid().nullish(),
  // SP: the salesperson assigned to this user (any live user; defaults from the user's school). Opaque id.
  salespersonId: z.guid().nullish(),
  createdAt: isoDate,
  updatedAt: isoDate,
  profile: userExtendedProfileSchema.nullish(),
  addresses: z.array(userAddressSchema),
  roles: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? [])
});
export type UserDetail = z.infer<typeof userDetailSchema>;

/**
 * PUT /api/v1/users/{id} body (admin edit, identity.users.update) — name parts + demographics +
 * the UF-1 extended fields. Every field optional; a null/omitted field clears the attribute.
 * PX: `passportNumber` is deliberately ABSENT — write it through PUT /users/{id}/passport, which
 * requires `identity.users.sensitive_update`.
 */
export interface AdminUpdateUserRequest {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  countryDefinitionId?: string | null;
  birthDate?: string | null;
  genderDefinitionId?: string | null;
  parentGuardianEmail?: string | null;
  parentGuardianPhone?: string | null;
  shirtSizeDefinitionId?: string | null;
  mealPreferenceDefinitionId?: string | null;
  preferredLanguageDefinitionId?: string | null;
  teacherUserId?: string | null;
  /** AGECAT-PT: as {@link UserDemographicsRequest.ageCategories} — absent leaves the set alone, [] clears it. */
  ageCategories?: UserAgeCategory[] | null;
  /** SAC: true ⇒ the placements above are the manual override; false ⇒ the client derives them from birthDate. */
  specialAgeCategory: boolean;
  schoolId?: string | null;
  /** SP: the salesperson assigned to this user (any live user). null clears it. */
  salespersonId?: string | null;
  teeneagleParentUserId?: string | null;
}

/**
 * ADM-MAIL — PUT /api/v1/users/{userId}/email body (identity.users.update).
 *
 * ⚠ A SEPARATE CALL FROM {@link AdminUpdateUserRequest}, and it must stay one. That request is
 * null-through (an omitted or blank field CLEARS the attribute); applied to the sign-in address that
 * would delete the only credential of an e-mail-only account and its only recovery path. The server
 * refuses a blank here, and the edit form sends this only when the value actually changed.
 *
 * ⚠ It sends NO MAIL. Verified status resets and every live one-time link for that user is consumed
 * (a link in the OLD mailbox would otherwise act on the NEW address) — then nothing goes out until
 * somebody presses "E-posta Gönder", which is {@link SendUserAccountEmailRequest}.
 */
export interface SetUserEmailRequest {
  email: string;
}

/** The address after the write. `emailVerified` is always false when the address actually changed. */
export const userEmailResultSchema = z.object({
  userId: z.guid(),
  email: z.string(),
  emailVerified: z.boolean()
});
export type UserEmailResult = z.infer<typeof userEmailResultSchema>;

/**
 * ADM-MAIL — the two mails an admin can ask for. ⚠ TWO KINDS, THREE PURPOSES: 'verify' resolves
 * server-side to an activation link when the account has no password yet (activation and email_verify
 * are exact inverses on `password_hash`, so the wrong one is a link the redemption endpoint refuses —
 * a mail that looks sent and cannot work). Do NOT add 'activation' here; the server rejects it with a
 * 400 (validation-failed) on purpose, because the account's state is what decides, not the caller.
 */
export const userAccountEmailKinds = ['verify', 'password_reset'] as const;
export type UserAccountEmailKind = (typeof userAccountEmailKinds)[number];

/** POST /api/v1/users/{userId}/account-email body (identity.users.update). */
export interface SendUserAccountEmailRequest {
  kind: UserAccountEmailKind;
}

/**
 * WHICH mail actually went out — named after the PURPOSE, not the request, because 'verify' has two
 * outcomes and the confirmation would otherwise be a guess. Kept a plain string rather than an enum so
 * a server that grows a fourth purpose does not fail the parse and blank the dialog.
 */
export const userAccountEmailResultSchema = z.object({
  sent: z.string()
});
export type UserAccountEmailResult = z.infer<typeof userAccountEmailResultSchema>;

/** PhoneVerificationStartResult — POST /users/me/phone/verify/start (#2). Kod DÖNMEZ. */
export const phoneVerificationStartSchema = z.object({
  expiresAtUtc: isoDate
});

/** ConsentDto — KVKK rıza defteri güncel durumu (137). */
export const consentSchema = z.object({
  purpose: z.string(),
  granted: z.boolean(),
  policyVersion: z.string(),
  updatedAtUtc: isoDate
});

/**
 * KVKK rıza amaçları (FE-8c "KVKK İzinlerim") — backend ConsentPurposes ile birebir. terms_of_service /
 * privacy_policy zorunlu (geri çekilemez sayılabilir, sunucu doğrular); pazarlama/analitik/veli izni
 * kullanıcı denetimindedir.
 */
export const consentPurposes = [
  'terms_of_service',
  'privacy_policy',
  'marketing_email',
  'marketing_sms',
  'analytics',
  'parental_consent'
] as const;
export type ConsentPurpose = (typeof consentPurposes)[number];

/** GET /users/me/consents — güncel rıza defteri (amaç başına tek satır). */
export const consentListSchema = z.array(consentSchema);

/**
 * PUT /users/me/consents body (FE-8c). policyVersion, o amaç için gösterilen politikanın
 * sürümüdür (sunucudan gelen ConsentDto.policyVersion geri gönderilir). Yazma idempotent [STD-API-4].
 */
export interface UpdateConsentRequest {
  purpose: string;
  granted: boolean;
  policyVersion: string;
}

/** MyDeviceDto — GET/POST /users/me/devices (S-12.b). Token yanıtta ASLA dönmez. */
export const myDeviceSchema = z.object({
  id: z.guid(),
  platform: z.enum(['ios', 'android', 'web']),
  enabled: z.boolean(),
  createdAt: isoDate,
  lastSeenAt: isoDate.nullish()
});

/** ChangePasswordResult (P1-3a). */
export const changePasswordResultSchema = z.object({
  userId: z.guid(),
  changedAt: isoDate,
  revokedSessionCount: z.number()
});

/** MySessionDto — cihaz listesi satırı (P1-3b). */
export const mySessionSchema = z.object({
  id: z.guid(),
  createdAt: isoDate,
  lastSeenAt: z.string().nullish(),
  expiresAt: isoDate,
  ipAddress: z.string().nullish(),
  userAgent: z.string().nullish(),
  isCurrent: z.boolean()
});

export const mySessionListSchema = pagedListSchema(mySessionSchema);

/** MySessionRevokedResult (P1-3b). */
export const mySessionRevokedSchema = z.object({
  sessionId: z.guid(),
  revokedAt: isoDate,
  wasCurrent: z.boolean()
});

/** MfaMethodDto + MfaStatusDto — GET /users/me/mfa (P1-3c). */
export const mfaMethodSchema = z.object({
  method: z.string(),
  status: z.enum(['pending', 'enabled']),
  enabledAt: z.string().nullish(),
  lastUsedAt: z.string().nullish()
});

export const mfaStatusSchema = z.object({
  methods: z.array(mfaMethodSchema),
  backupCodesRemaining: z.number()
});

/** TotpEnrollmentDto — secret/otpauth SADECE bu yanıtta (P1-3c). */
export const totpEnrollmentSchema = z.object({
  secretBase32: z.string(),
  otpauthUri: z.string(),
  createdAt: isoDate
});

/** MfaVerifiedDto — yedek kodların ham hali SADECE burada (P1-3c). */
export const mfaVerifiedSchema = z.object({
  method: z.string(),
  enabledAt: isoDate,
  backupCodes: z.array(z.string())
});

/** MfaDisabledDto (P1-3c). */
export const mfaDisabledSchema = z.object({
  method: z.string(),
  disabledAt: isoDate
});

export type MyProfile = z.infer<typeof myProfileSchema>;
export type MyDevice = z.infer<typeof myDeviceSchema>;
export type PhoneVerificationStart = z.infer<typeof phoneVerificationStartSchema>;
export type Consent = z.infer<typeof consentSchema>;
export type MySession = z.infer<typeof mySessionSchema>;
export type MfaStatus = z.infer<typeof mfaStatusSchema>;
export type MfaMethod = z.infer<typeof mfaMethodSchema>;
export type TotpEnrollment = z.infer<typeof totpEnrollmentSchema>;
export type MfaVerified = z.infer<typeof mfaVerifiedSchema>;

// =============================================================================
// P5-ID — ADMIN account recovery. A platform admin can read a user's security posture
// and reset their MFA (no secrets exposed). Self-reset is blocked server-side (409).
// =============================================================================

/** AdminMfaMethodDto — one MFA method as an admin sees it (no secret). */
export const adminMfaMethodSchema = z.object({
  method: z.string(),
  status: z.string(),
  verifiedAt: isoDate.nullish(),
  lastUsedAt: isoDate.nullish()
});

/** UserSecurityDto — GET /users/{id}/security. */
export const userSecuritySchema = z.object({
  userId: z.guid(),
  mfaEnabled: z.boolean(),
  methods: z.array(adminMfaMethodSchema),
  backupCodesRemaining: z.number(),
  activeSessionCount: z.number()
});

/** UserMfaResetDto — POST /users/{id}/mfa/reset. */
export const userMfaResetSchema = z.object({
  userId: z.guid(),
  resetAt: isoDate,
  methodsRemoved: z.number()
});

export type AdminMfaMethod = z.infer<typeof adminMfaMethodSchema>;
export type UserSecurity = z.infer<typeof userSecuritySchema>;
export type UserMfaReset = z.infer<typeof userMfaResetSchema>;

/** AdminSessionDto — a user's live session as an admin sees it (P5-ID+; no token/hash). */
export const adminSessionSchema = z.object({
  id: z.guid(),
  createdAt: isoDate,
  lastSeenAt: isoDate.nullish(),
  expiresAt: isoDate,
  ipAddress: z.string().nullish(),
  userAgent: z.string().nullish()
});
export const adminSessionListSchema = pagedListSchema(adminSessionSchema);

/** AdminSessionsRevokedDto — POST /users/{id}/sessions/revoke. */
export const adminSessionsRevokedSchema = z.object({
  userId: z.guid(),
  revokedCount: z.number()
});

export type AdminSession = z.infer<typeof adminSessionSchema>;
export type AdminSessionsRevoked = z.infer<typeof adminSessionsRevokedSchema>;

// ---- LOGIN-LINK (Identity mig 559) ----
// A single-use sign-in link an admin mints for a user. Whoever opens it first is signed in AS that user,
// access token only, until `expiresAt` (≤ 2 h after creation). Gated by identity.impersonation.start.

/** Policy bounds — the server validates the same range and the DB CHECKs the ceiling. */
export const USER_LOGIN_LINK_MIN_MINUTES = 1;
export const USER_LOGIN_LINK_MAX_MINUTES = 120;

/** POST /api/v1/users/{id}/login-links body. `reason` is mandatory (audit), ≤ 500 chars. */
export interface CreateUserLoginLinkRequest {
  minutes: number;
  reason: string;
}

/** LoginLinkCreatedDto — `token` is returned ONCE and can never be fetched again. */
export const userLoginLinkCreatedSchema = z.object({
  id: z.guid(),
  userId: z.guid(),
  token: z.string(),
  expiresAt: isoDate
});

/** 'live' | 'used' | 'revoked' | 'expired' — kept a plain string so a new state cannot blank the list. */
export const userLoginLinkSchema = z.object({
  id: z.guid(),
  status: z.string(),
  reason: z.string(),
  createdBy: z.guid(),
  createdAt: isoDate,
  expiresAt: isoDate,
  consumedAt: isoDate.nullish(),
  consumedIp: z.string().nullish(),
  consumedUserAgent: z.string().nullish(),
  sessionId: z.guid().nullish(),
  revokedAt: isoDate.nullish()
});
export const userLoginLinkListSchema = z.array(userLoginLinkSchema);

export type UserLoginLinkCreated = z.infer<typeof userLoginLinkCreatedSchema>;
export type UserLoginLink = z.infer<typeof userLoginLinkSchema>;

// ---- B-Core impersonation (§10.5-20) ----
/** ImpersonationTokenDto — POST /api/v1/admin/impersonation. Short-lived, no refresh; stamps impersonator. */
export const impersonationTokenSchema = z.object({
  accessToken: z.string(),
  tokenType: z.string(),
  expiresAtUtc: z.string().min(1),
  sessionId: z.guid(),
  targetUserId: z.guid(),
  impersonatorUserId: z.guid()
});

/** StartImpersonationCommand body (reason required for accountability). */
export interface StartImpersonationRequest {
  targetUserId: string;
  reason: string;
}

export type ImpersonationToken = z.infer<typeof impersonationTokenSchema>;

// ==============================|| RSU — ROLE APPLICATION REVIEW QUEUE ||========================= //
//
// A self-registered user's application for an elevated role (migration 300). Today only
// 'partner_employee': a request to join an existing partner company as staff. Approval is the ONLY
// thing that grants the role and links identity.users.company_id — so the reviewer's decision is a
// real authorization act, not a bookkeeping flag.
//
// Teachers never appear here: teacher signup is instant on e-mail verification.

export const roleRequestStatuses = ['pending', 'approved', 'rejected'] as const;
export type RoleRequestStatus = (typeof roleRequestStatuses)[number];

// ==============================|| BEMORE — ROLE APPLICANT FORMS ||=============================== //
//
// A tenant admin gives a ROLE an application form (migration 460); a visitor with no account picks
// that role on /register/bemore, answers the questions, and an admin decides. Approval CREATES the
// user with that role and mails an activation link.
//
// The question shape mirrors Engagement's event questions so one renderer serves both surfaces —
// MINUS 'profile' (an applicant has no profile to auto-fill from) PLUS 'definition'.

/**
 * ⚠ `definition` IS A DROPDOWN WHOSE OPTIONS WERE COPIED, not a live binding. The admin's browser
 * reads a Definitions group and posts the values as ordinary `options`; Identity cannot read that
 * schema itself (no grant, no gRPC edge), so there is no server-side refresh and no membership check.
 * Saving the question re-takes the copy.
 *
 * ⚠ NO `profile` MEMBER, unlike `eventQuestionTypes`. Copying that list wholesale is the mistake to
 * avoid: a profile question auto-fills from the answering user, and a Be More applicant has none.
 */
export const roleApplicationQuestionTypes = [
  'text',
  'textarea',
  'number',
  'select',
  'multiselect',
  'toggle',
  'date',
  'definition'
] as const;
export type RoleApplicationQuestionType = (typeof roleApplicationQuestionTypes)[number];

/** Types whose `options` array carries the choices an answer must come from. */
export const roleApplicationOptionTypes: readonly RoleApplicationQuestionType[] = [
  'select',
  'multiselect',
  'definition'
];

export const roleApplicationOptionSchema = z.object({
  value: z.string(),
  label: z.string()
});

export const roleApplicationQuestionSchema = z.object({
  id: z.string(),
  label: z.string(),
  type: z.enum(roleApplicationQuestionTypes),
  isRequired: z.boolean(),
  isActive: z.boolean(),
  orderNo: z.number(),
  options: z.array(roleApplicationOptionSchema),
  /** `definition` only: the Definitions group the options were copied from. */
  definitionGroupKey: z.string().nullish(),
  /** When that copy was taken, so the builder can show its age. */
  definitionSyncedAt: z.string().nullish()
});

/**
 * One answer. ON SUBMIT only `questionId` and `value` are sent; the server stamps `label`, `type` and
 * `displayValue` from the live question and stores them alongside — so a decided application stays
 * readable after the form is edited, and a client cannot choose the wording it is filed under.
 *
 * `value` is a JSON scalar or a string array (multiselect). For a `definition` answer it is the
 * definition's OPAQUE id, and `displayValue` carries the name it was chosen under — which is why the
 * review screen shows "Türkiye" rather than a uuid.
 */
export const roleApplicationAnswerSchema = z.object({
  questionId: z.string(),
  value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]),
  label: z.string().nullish(),
  type: z.string().nullish(),
  displayValue: z.string().nullish()
});

/** A role and its form, if it has one — one row of the builder's left-hand list. */
export const roleApplicationFormSchema = z.object({
  roleId: z.string(),
  roleCode: z.string(),
  roleName: z.string(),
  /**
   * ⚠ A role with NO type cannot publish a form: its holder would activate, sign in and land in no
   * SPA at all. The builder says so rather than letting the server 422 explain it.
   */
  roleType: z.string().nullish(),
  formId: z.string().nullish(),
  description: z.string().nullish(),
  infoTitle: z.string().nullish(),
  infoBody: z.string().nullish(),
  isActive: z.boolean(),
  questions: z.array(roleApplicationQuestionSchema)
});

export const roleApplicationFormListSchema = z.array(roleApplicationFormSchema);

export type RoleApplicationOption = z.infer<typeof roleApplicationOptionSchema>;
export type RoleApplicationQuestion = z.infer<typeof roleApplicationQuestionSchema>;
export type RoleApplicationAnswer = z.infer<typeof roleApplicationAnswerSchema>;
export type RoleApplicationForm = z.infer<typeof roleApplicationFormSchema>;

/** Body for PUT /api/v1/role-application-forms/{roleId}. */
export interface UpsertRoleApplicationFormBody {
  description: string | null;
  infoTitle: string | null;
  infoBody: string | null;
  isActive: boolean;
}

/** Body for the question create/update endpoints — one shape for both, as the server has. */
export interface RoleApplicationQuestionBody {
  label: string;
  type: RoleApplicationQuestionType;
  isRequired: boolean;
  isActive: boolean;
  orderNo: number;
  options: RoleApplicationOption[] | null;
  definitionGroupKey: string | null;
}

/**
 * BEMORE: which flow filed the application.
 * - `partner_link` — migration 300's original: a signed-up USER asking to join a partner company.
 * - `form` — an ANONYMOUS applicant answering a role's applicant form. No account until approval.
 */
export const roleRequestKinds = ['partner_link', 'form'] as const;
export type RoleRequestKind = (typeof roleRequestKinds)[number];

export const roleRequestSchema = z.object({
  id: z.string(),
  // ⚠ NULLISH SINCE BEMORE (mig 461), and null is the NORMAL case for a form application: the
  // applicant has no account until an admin approves. This was `z.string()`, which would have failed
  // parsing on every Be More row — and because the queue parses a LIST, one bad row blanks the whole
  // page rather than one cell.
  userId: z.string().nullish(),
  // Resolved server-side so the queue is readable without a per-row user fetch. For a form
  // application these come from the application row itself, not from a user.
  applicantName: z.string(),
  applicantEmail: z.string(),
  requestedRole: z.string(),
  requestedCompanyId: z.string().nullish(),
  companyName: z.string().nullish(),
  status: z.enum(roleRequestStatuses),
  rejectionReason: z.string().nullish(),
  decidedAt: z.string().nullish(),
  decidedBy: z.string().nullish(),
  // NULLISH ON PURPOSE: decided_by carries no FK so the audit trail survives the admin who made the
  // decision — once that account is gone the name is genuinely unknown, not an error.
  decidedByName: z.string().nullish(),
  createdAt: z.string(),
  // ---- BEMORE. All optional so a response from a pre-cutover build still parses. ----
  kind: z.enum(roleRequestKinds).nullish(),
  roleId: z.string().nullish(),
  roleName: z.string().nullish(),
  /** How many questions were answered, over how many the form asks TODAY. */
  answerCount: z.number().nullish(),
  questionCount: z.number().nullish(),
  /**
   * The applicant's address already belongs to a user here, so approval is refused (409). DERIVED
   * server-side per read — an account can appear or vanish while the row waits.
   */
  emailAlreadyInUse: z.boolean().nullish(),
  /** The account approval created, once it has. */
  createdUserId: z.string().nullish(),
  /** Filled by the DETAIL read only; the list omits answer bodies. */
  answers: z.array(roleApplicationAnswerSchema).nullish()
});

export const roleRequestListSchema = pagedListSchema(roleRequestSchema);

export type RoleRequest = z.infer<typeof roleRequestSchema>;

/** Body for POST /role-requests/{id}/reject — the reason is optional and reaches the applicant. */
export interface RejectRoleRequestBody {
  reason?: string;
}

// ==============================|| DASH-P4 — admin Overview: Identity numbers ||============================== //
// GET /api/v1/identity/dashboard/summary?from=YYYY-MM-DD&to=YYYY-MM-DD&timeZone= — identity.users.view,
// [NoCache]. "A user" is what the Users list counts (not deleted, system accounts included).
//   • newUsers.byRoleType is ANY-of — one person holding two role types counts under both, so the rows
//     do NOT sum to `created.current`. Never add them up on the page.
//   • invited is a NOW value (open invitations, whenever sent); the period does not move it.
//   • roleRequests is NULL — not an empty queue — for a caller without identity.role_requests.view.
//   • role type / role codes are plain strings: a Zod enum would reject the whole response the day the
//     server adds a value mid-deploy.

const identityPeriodCount = z.object({ current: z.number().int(), previous: z.number().int() });

export const identityDashboardSummarySchema = z.object({
  /** DASH-P5 filter bar — people have no programme; role requests have no segment at all */
  filter: dashboardFilterSchema.nullish(),
  period: z.object({ from: z.string(), to: z.string(), previousFrom: z.string(), previousTo: z.string(), timeZone: z.string(), previousUntil: z.string().nullish() }),
  newUsers: z.object({
    created: identityPeriodCount,
    byRoleType: z.array(z.object({ key: z.string(), count: z.number().int() }))
  }),
  invited: z.object({ total: z.number().int(), olderThanWeek: z.number().int(), oldestCreatedAtUtc: z.string().nullable() }),
  roleRequests: z
    .object({
      pending: z.number().int(),
      oldestCreatedAtUtc: z.string().nullable(),
      /** `name` is the tenant role's display name; null for the partner-link code, which the page labels */
      byRole: z.array(z.object({ role: z.string(), name: z.string().nullable(), count: z.number().int() })),
      approved: identityPeriodCount,
      rejected: identityPeriodCount
    })
    .nullable()
});

export type IdentityDashboardSummary = z.infer<typeof identityDashboardSummarySchema>;

// ---- USER-CODE (mig 629) -----------------------------------------------------------------------------
/**
 * GET/PUT /api/v1/identity/user-code-settings — the prefix of every user's 9-character code. Shown on the
 * admin Branding page but owned by Identity (Branding lives in ControlPlane, which Identity cannot read
 * while creating a user). Numbers 1..`reservedThrough` are reserved and only ever set in the database.
 */
export const userCodeSettingsSchema = z.object({
  /** null = no prefix yet: new users get no code until one is saved. */
  prefix: z.string().nullish(),
  /** the number the next new user gets; null with no prefix, or when the series is exhausted */
  nextNumber: z.number().int().nullish(),
  issuedCount: z.number().int(),
  remaining: z.number().int(),
  reservedThrough: z.number().int(),
  /** only on the PUT response: how many existing users received a code */
  backfilled: z.number().int().nullish()
});
export type UserCodeSettings = z.infer<typeof userCodeSettingsSchema>;
